import React, { useState, useMemo } from 'react'
import { format } from 'date-fns'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CalendarDays, Plus, Trash2 } from 'lucide-react'
import type { ScheduleException } from '@/services/bookingService'
import ConfirmDeleteDialog from '@/components/ui/confirm-delete-dialog'
import { formatToLocalDate } from '@/lib/utils'

type DeleteTarget =
  | { kind: 'single'; exception: ScheduleException }
  | { kind: 'group'; groupId: string; label: string }

interface RangeGroup {
  groupId: string
  exceptions: ScheduleException[]
  startDate: string
  endDate: string
  notes?: string
}

export interface ScheduleExceptionsListProps {
  exceptions: ScheduleException[]
  onAdd: () => void
  onDelete: (exceptionId: string) => void
  onDeleteGroup?: (groupId: string) => void
  formatTime?: (time: string) => string
}

export const ScheduleExceptionsList: React.FC<ScheduleExceptionsListProps> = ({
  exceptions,
  onAdd,
  onDelete,
  onDeleteGroup,
  formatTime,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)

  const defaultFormatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const h = parseInt(hours)
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    const ampm = h < 12 ? 'AM' : 'PM'
    return `${h12}:${minutes} ${ampm}`
  }

  const timeFormatter = formatTime || defaultFormatTime

  const { rangeGroups, individualExceptions } = useMemo(() => {
    const groupMap = new Map<string, ScheduleException[]>()
    const singles: ScheduleException[] = []

    for (const ex of exceptions) {
      if (ex.range_group_id) {
        const list = groupMap.get(ex.range_group_id) ?? []
        list.push(ex)
        groupMap.set(ex.range_group_id, list)
      } else {
        singles.push(ex)
      }
    }

    const groups: RangeGroup[] = []
    for (const [groupId, items] of groupMap) {
      const sorted = items.sort(
        (a, b) => new Date(a.exception_date).getTime() - new Date(b.exception_date).getTime(),
      )
      groups.push({
        groupId,
        exceptions: sorted,
        startDate: sorted[0].exception_date,
        endDate: sorted[sorted.length - 1].exception_date,
        notes: sorted[0].notes,
      })
    }

    groups.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())

    return { rangeGroups: groups, individualExceptions: singles }
  }, [exceptions])

  const rangeLabel = (group: RangeGroup) => {
    const start = formatToLocalDate(group.startDate, 'MMMM DD, YYYY', true)
    const end = formatToLocalDate(group.endDate, 'MMMM DD, YYYY', true)
    return `${start} \u2014 ${end}`
  }

  const deleteDialogLabel = () => {
    if (!deleteTarget) return undefined
    if (deleteTarget.kind === 'single') {
      return format(new Date(deleteTarget.exception.exception_date), 'EEEE, MMMM d, yyyy')
    }
    return deleteTarget.label
  }

  if (exceptions.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No schedule exceptions</p>
        <p className="text-sm">Add time off or custom hours for specific dates</p>
        <Button variant="outline" className="mt-4" onClick={onAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Add Exception
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Range groups */}
      {rangeGroups.map((group) => (
        <div
          key={group.groupId}
          className="flex items-center justify-between p-4 border rounded-xl hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Badge variant="destructive">Unavailable</Badge>
            <div className="min-w-0">
              <p className="font-medium">{rangeLabel(group)}</p>
              <p className="text-sm text-slate-500">
                {group.exceptions.length} day{group.exceptions.length !== 1 ? 's' : ''} blocked
              </p>
              {group.notes && (
                <p className="text-sm text-slate-500 truncate">{group.notes}</p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
            onClick={() => {
              setDeleteTarget({
                kind: 'group',
                groupId: group.groupId,
                label: rangeLabel(group),
              })
              setDeleteDialogOpen(true)
            }}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Remove
          </Button>
        </div>
      ))}

      {/* Individual exceptions */}
      {individualExceptions.map((exception) => (
        <div
          key={exception.id}
          className="flex items-center justify-between p-4 border rounded-xl hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Badge variant={exception.is_available ? 'secondary' : 'destructive'}>
              {exception.exception_type === 'time_off' ? 'Day Off' : 'Custom Hours'}
            </Badge>
            <div className="min-w-0">
              <p className="font-medium">
                {formatToLocalDate(exception.exception_date, 'dddd, MMMM DD, YYYY', true)}
              </p>
              {exception.exception_type === 'custom_hours' && exception.start_time && exception.end_time && (
                <p className="text-sm text-slate-500">
                  {timeFormatter(exception.start_time)} - {timeFormatter(exception.end_time)}
                </p>
              )}
              {exception.notes && (
                <p className="text-sm text-slate-500 truncate">{exception.notes}</p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
            onClick={() => {
              setDeleteTarget({ kind: 'single', exception })
              setDeleteDialogOpen(true)
            }}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Remove
          </Button>
        </div>
      ))}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={
          deleteTarget?.kind === 'group'
            ? 'Remove blocked date range?'
            : 'Remove schedule exception?'
        }
        description={
          deleteTarget?.kind === 'group'
            ? 'This will remove all blocked dates in this range and restore default availability.'
            : 'This will remove the exception and restore default availability for that date.'
        }
        itemName={deleteDialogLabel()}
        itemType="exception"
        onConfirm={async () => {
          if (!deleteTarget) return
          if (deleteTarget.kind === 'single') {
            await onDelete(deleteTarget.exception.id)
          } else if (onDeleteGroup) {
            await onDeleteGroup(deleteTarget.groupId)
          }
          setDeleteTarget(null)
        }}
        successMessage={
          deleteTarget?.kind === 'group'
            ? 'Blocked date range removed.'
            : 'Exception removed.'
        }
      />
    </div>
  )
}
